// デプロイ直後に古いタブが参照する JS チャンク（コンテンツハッシュ付き）が
// 新ビルドで別ハッシュに変わり 404 になることで ChunkLoadError が発生する。
// この種のエラーは全体を再読み込みすれば新版の HTML・チャンクを取り直して復旧できる。
//
// 復旧フロー（error.tsx から利用）:
//
//   ChunkLoadError か？ ──No──▶ 報告 → エラー画面
//        │Yes
//        ▼
//   直近ウィンドウ内に再読み込み記録あり？（記憶域不可なら「あり」扱い）
//      │Yes（本質的失敗）        │No（一時的失敗）
//      ▼                          ▼
//   報告 → エラー画面          記録保存 → 報告せず再読み込み

// 再読み込みのループ防止に用いる時間ウィンドウ。新版の HTML・チャンク取得が
// 確実に間に合う範囲とする。これより短い間隔で ChunkLoadError が再発した場合は
// 「再読み込みしても直らない本質的な失敗」とみなして再読み込みを打ち切る。
const RELOAD_WINDOW_MS = 10_000;
const RELOAD_TS_KEY = "chunk-reload-ts";

/**
 * ChunkLoadError かどうかを判定する。
 * 実ログで name=ChunkLoadError / message=Failed to load chunk ... の双方を
 * 確認済みのため、エラー名とメッセージの両方で判定して取りこぼしを防ぐ。
 * CSS チャンクの読み込み失敗（"Loading CSS chunk N failed"）はエラー名が
 * ChunkLoadError にならないためメッセージで補足する。
 * backend/app/controllers/api/v1/client_errors_controller.rb の
 * CHUNK_LOAD_MESSAGE_PATTERN と同じ判定。変更する際はもう一方との整合を
 * 確認すること。
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.name === "ChunkLoadError") {
    return true;
  }
  return /loading (css )?chunk|failed to load chunk/i.test(
    error.message ?? "",
  );
}

/**
 * 直近 RELOAD_WINDOW_MS 以内に自動再読み込みを実行済みかを判定する。
 * セッションストレージ参照は Safari のプライベートモード等で例外を投げるため
 * try/catch で保護し、参照できない場合は「再読み込み済み（= これ以上再読み込み
 * しない）」側に倒す。エラー境界内での例外再発による白画面を防ぐ。
 */
export function hasRecentlyReloaded(now: number): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    const raw = window.sessionStorage.getItem(RELOAD_TS_KEY);
    if (raw === null) {
      return false;
    }
    const last = Number(raw);
    if (!Number.isFinite(last)) {
      return false;
    }
    return now - last < RELOAD_WINDOW_MS;
  } catch {
    return true;
  }
}

/**
 * 自動再読み込みの実行時刻を記録する。
 * 記憶域へ書き込めない場合でも例外を投げない（再読み込みするかどうかの判断は
 * hasRecentlyReloaded 側で行うため、書き込み失敗は黙って無視してよい）。
 */
export function markReloaded(now: number): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
  } catch {
    // 記憶域へ書き込めない環境では何もしない
  }
}
